import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../../config';
import {
    CompletionStatusChange,
    DepartedVehiclesPage,
    IncomingVehiclesPage,
    InventoryVehiclesPage,
    LoadPreviewArrivals,
    LoadPreviewDepartures,
    LoadPreviewUnannouncedDepartures,
    LoadValidationRequest,
    OutgoingVehiclesPage,
} from '../compoundManagement/compoundManagement.types';
import type { DepartedVehiclesTableColumn } from '../compoundManagement/departed/table/DepartedVehiclesTableColumn';
import type { IncomingVehiclesTableColumn } from '../compoundManagement/incoming/table/IncomingVehiclesTableColumn';
import type { VehicleInventoryTableColumn } from '../compoundManagement/inventory/table/VehicleInventoryTableColumn';
import type { OutgoingVehiclesTableColumn } from '../compoundManagement/outgoing/table/OutgoingVehiclesTableColumn';
import { CompoundVehicleType, FileExport, MeansOfTransportType, VehicleType } from '../sharedComponents/common.types';
import { TableSort } from '../sharedComponents/table/SortedTableHeader';
import { mapToApiStatusCompletion } from './mappers/compoundManagement/completionMapper';
import {
    mapToApiLoadValidationRequest,
    mapToCompoundVehicleIds,
    mapToDepartedVehiclesPage,
    mapToIncomingVehiclesPage,
    mapToInventoryVehiclesPage,
    mapToLoadPreviewArrivals,
    mapToLoadPreviewDepartures,
    mapToLoadPreviewUnannouncedDepartures,
    mapToOutgoingVehiclesPage,
} from './mappers/compoundManagement/compoundManagementMappers';
import { decodeResponse, validateResponse } from './responseUtil';
import {
    DepartedVehiclesPageCodec,
    IncomingVehiclesPageCodec,
    InventoryVehiclesPageCodec,
    LoadPreviewCodec,
    OutgoingVehiclesPageCodec,
    UnannouncedLoadPreviewDeparturesCodec,
    VehicleBulkSelectionCodec,
} from './types/compoundManagement/compoundManagementApi.types';
import { prepareHeaders } from './utils';
import { mapToCompoundVehicleExportQueryParameters } from './mappers/compoundManagement/queryParameterMappers';
import { FILE_NAME_REGEX } from './transportOrderApi';
import { CONTENT_TYPE_EXCEL } from './serviceOrdersApi';
import {
    mapToDepartedVehiclesRequestBody,
    mapToIncomingVehicleIdsRequestBody,
    mapToIncomingVehiclesRequestBody,
    mapToInventoryVehicleIdsRequestBody,
    mapToInventoryVehiclesRequestBody,
    mapToOutgoingVehicleIdsRequestBody,
    mapToOutgoingVehiclesRequestBody,
} from './mappers/compoundManagement/requestBodyMappers';

export const COMPOUND_MANAGEMENT_INCOMING_VEHICLES_PATH = 'compound-incoming-vehicles';
export const COMPOUND_MANAGEMENT_INCOMING_VEHICLE_IDS_PATH = 'compound-incoming-vehicle-ids';
export const COMPOUND_MANAGEMENT_INVENTORY_VEHICLE_IDS_PATH = 'compound-inventory-vehicle-ids';
export const COMPOUND_MANAGEMENT_INVENTORY_VEHICLES_PATH = 'compound-inventory-vehicles';
export const COMPOUND_MANAGEMENT_OUTGOING_VEHICLES_PATH = 'compound-outgoing-vehicles';
export const COMPOUND_MANAGEMENT_OUTGOING_VEHICLE_IDS_PATH = 'compound-outgoing-vehicle-ids';
export const COMPOUND_MANAGEMENT_DEPARTED_VEHICLES_PATH = 'compound-departed-vehicles';
export const COMPOUND_MANAGEMENT_VEHICLES_PATH = 'compound-vehicles';
export const COMPOUND_MANAGEMENT_COMPOUND_VEHICLES_EXPORT_PATH = 'exports';
export const COMPOUND_MANAGEMENT_ARRIVAL_COMPLETIONS_PATH = 'arrival-completions';
export const COMPOUND_MANAGEMENT_DEPARTURE_COMPLETIONS_PATH = 'departure-completions';
export const COMPOUND_MANAGEMENT_LOAD_PREVIEW_ARRIVALS_PATH = 'load-preview-arrivals';
export const COMPOUND_MANAGEMENT_LOAD_PREVIEW_UNANNOUNCED_DEPARTURES_PATH = 'load-preview-unannounced-departures';
export const COMPOUND_MANAGEMENT_LOAD_PREVIEW_DEPARTURES_PATH = 'load-preview-departures';

export type GetIncomingVehiclesQueryParams = {
    location?: string;
    vehicleType?: VehicleType;
    manufacturer?: string;
    expectedArrivalDateStartAt?: Date;
    expectedArrivalDateEndAt?: Date;
    loadId?: string;
    meansOfTransportId?: string;
    finalDestination?: string;
    vins?: string[];
    excludeCancelled?: boolean;
    arrivalFromLocation?: string;
    arrivalMeansOfTransportType?: MeansOfTransportType;
    sortBy?: TableSort<IncomingVehiclesTableColumn>;
    limit?: number;
};

export type GetInventoryVehiclesFilterQueryParams = {
    location?: string;
    vehicleType?: VehicleType;
    manufacturer?: string;
    arrivalDateStartAt?: Date;
    arrivalDateEndAt?: Date;
    finalDestination?: string;
    vins?: string[];
    excludeCancelled?: boolean;
    arrivalFromLocation?: string;
    arrivalMeansOfTransportType?: MeansOfTransportType;
};

export type GetInventoryVehiclesQueryParams = GetInventoryVehiclesFilterQueryParams & {
    sortBy?: TableSort<VehicleInventoryTableColumn>;
    limit?: number;
};

export type GetOutgoingVehiclesFilterQueryParams = {
    location?: string;
    vehicleType?: VehicleType;
    manufacturer?: string;
    expectedDepartureDateStartAt?: Date;
    expectedDepartureDateEndAt?: Date;
    loadId?: string;
    meansOfTransportId?: string;
    finalDestination?: string;
    vins?: string[];
    excludeCancelled?: boolean;
    departureToLocation?: string;
    departureMeansOfTransportType?: MeansOfTransportType;
};

export type GetOutgoingVehiclesQueryParams = GetOutgoingVehiclesFilterQueryParams & {
    sortBy?: TableSort<OutgoingVehiclesTableColumn>;
    limit?: number;
};

export type GetDepartedVehiclesQueryParams = {
    location?: string;
    vehicleType?: VehicleType;
    manufacturer?: string;
    arrivalDateStartAt?: Date;
    arrivalDateEndAt?: Date;
    departureDateStartAt?: Date;
    departureDateEndAt?: Date;
    finalDestination?: string;
    vins?: string[];
    excludeCancelled?: boolean;
    arrivalFromLocation?: string;
    arrivalMeansOfTransportType?: MeansOfTransportType;
    departureToLocation?: string;
    departureMeansOfTransportType?: MeansOfTransportType;
    sortBy?: TableSort<DepartedVehiclesTableColumn>;
    limit?: number;
};

export type GetCompoundVehicleExportQueryParams = {
    location?: string;
    compoundVehicleTypes: CompoundVehicleType[];
};

const IncomingVehiclesTag = 'IncomingVehiclesTag';
const InventoryVehiclesTag = 'InventoryVehiclesTag';
const OutgoingVehiclesTag = 'OutgoingVehiclesTag';
const DepartedVehiclesTag = 'DepartedVehiclesTag';

export const compoundManagementApi = createApi({
    reducerPath: 'compoundManagementApi',
    baseQuery: fetchBaseQuery({ baseUrl: config.backend.FINISHED_VEHICLES_SERVICE, prepareHeaders }),
    tagTypes: [IncomingVehiclesTag, InventoryVehiclesTag, OutgoingVehiclesTag, DepartedVehiclesTag],
    // eslint-disable-next-line max-lines-per-function
    endpoints: (builder) => ({
        getIncomingVehicles: builder.query<IncomingVehiclesPage, GetIncomingVehiclesQueryParams>({
            query: (queryParameters) => ({
                url: COMPOUND_MANAGEMENT_INCOMING_VEHICLES_PATH,
                method: 'POST',
                body: mapToIncomingVehiclesRequestBody(queryParameters),
                validateStatus: validateResponse(IncomingVehiclesPageCodec),
            }),
            transformResponse: decodeResponse(IncomingVehiclesPageCodec, mapToIncomingVehiclesPage),
            providesTags: [IncomingVehiclesTag],
        }),
        getIncomingVehicleIds: builder.mutation<string[], GetIncomingVehiclesQueryParams>({
            query: (queryParameters) => ({
                url: COMPOUND_MANAGEMENT_INCOMING_VEHICLE_IDS_PATH,
                method: 'POST',
                body: mapToIncomingVehicleIdsRequestBody(queryParameters),
                validateStatus: validateResponse(VehicleBulkSelectionCodec),
            }),
            transformResponse: decodeResponse(VehicleBulkSelectionCodec, mapToCompoundVehicleIds),
        }),
        getInventoryVehicles: builder.query<InventoryVehiclesPage, GetInventoryVehiclesQueryParams>({
            query: (queryParameters) => ({
                url: COMPOUND_MANAGEMENT_INVENTORY_VEHICLES_PATH,
                method: 'POST',
                body: mapToInventoryVehiclesRequestBody(queryParameters),
                validateStatus: validateResponse(InventoryVehiclesPageCodec),
            }),
            transformResponse: decodeResponse(InventoryVehiclesPageCodec, mapToInventoryVehiclesPage),
            providesTags: [InventoryVehiclesTag],
        }),
        getInventoryVehicleIds: builder.mutation<string[], GetInventoryVehiclesFilterQueryParams>({
            query: (queryParameters) => ({
                url: COMPOUND_MANAGEMENT_INVENTORY_VEHICLE_IDS_PATH,
                method: 'POST',
                body: mapToInventoryVehicleIdsRequestBody(queryParameters),
                validateStatus: validateResponse(VehicleBulkSelectionCodec),
            }),
            transformResponse: decodeResponse(VehicleBulkSelectionCodec, mapToCompoundVehicleIds),
        }),
        getOutgoingVehicles: builder.query<OutgoingVehiclesPage, GetOutgoingVehiclesQueryParams>({
            query: (queryParameters) => ({
                url: COMPOUND_MANAGEMENT_OUTGOING_VEHICLES_PATH,
                method: 'POST',
                body: mapToOutgoingVehiclesRequestBody(queryParameters),
                validateStatus: validateResponse(OutgoingVehiclesPageCodec),
            }),
            transformResponse: decodeResponse(OutgoingVehiclesPageCodec, mapToOutgoingVehiclesPage),
            providesTags: [OutgoingVehiclesTag],
        }),
        getOutgoingVehicleIds: builder.mutation<string[], GetOutgoingVehiclesFilterQueryParams>({
            query: (queryParameters) => ({
                url: COMPOUND_MANAGEMENT_OUTGOING_VEHICLE_IDS_PATH,
                method: 'POST',
                body: mapToOutgoingVehicleIdsRequestBody(queryParameters),
                validateStatus: validateResponse(VehicleBulkSelectionCodec),
            }),
            transformResponse: decodeResponse(VehicleBulkSelectionCodec, mapToCompoundVehicleIds),
        }),
        getDepartedVehicles: builder.query<DepartedVehiclesPage, GetDepartedVehiclesQueryParams>({
            query: (queryParameters) => ({
                url: COMPOUND_MANAGEMENT_DEPARTED_VEHICLES_PATH,
                method: 'POST',
                body: mapToDepartedVehiclesRequestBody(queryParameters),
                validateStatus: validateResponse(DepartedVehiclesPageCodec),
            }),
            transformResponse: decodeResponse(DepartedVehiclesPageCodec, mapToDepartedVehiclesPage),
            providesTags: [DepartedVehiclesTag],
        }),
        getCompoundVehiclesExport: builder.mutation<FileExport, GetCompoundVehicleExportQueryParams>({
            query: (queryParameter) => ({
                url: `${COMPOUND_MANAGEMENT_VEHICLES_PATH}/${COMPOUND_MANAGEMENT_COMPOUND_VEHICLES_EXPORT_PATH}`,
                params: mapToCompoundVehicleExportQueryParameters(queryParameter),
                cache: 'no-cache',
                responseHandler: async (response): Promise<FileExport> => {
                    if (response.headers.get('content-type') === CONTENT_TYPE_EXCEL) {
                        return {
                            fileLink: window.URL.createObjectURL(await response.blob()),
                            fileName: response.headers.get('Content-Disposition')?.match(FILE_NAME_REGEX)?.at(1),
                        };
                    } else {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                        return response.json();
                    }
                },
            }),
        }),
        putArrivalCompletion: builder.mutation<void, CompletionStatusChange>({
            query: (statusChange) => ({
                url: `${COMPOUND_MANAGEMENT_VEHICLES_PATH}/${COMPOUND_MANAGEMENT_ARRIVAL_COMPLETIONS_PATH}`,
                method: 'PUT',
                body: mapToApiStatusCompletion(statusChange),
            }),
            invalidatesTags: [IncomingVehiclesTag, InventoryVehiclesTag, DepartedVehiclesTag],
        }),
        postLoadPreviewArrivals: builder.mutation<LoadPreviewArrivals, LoadValidationRequest>({
            query: (validationRequest) => ({
                url: COMPOUND_MANAGEMENT_LOAD_PREVIEW_ARRIVALS_PATH,
                method: 'POST',
                body: mapToApiLoadValidationRequest(validationRequest),
                validateStatus: validateResponse(LoadPreviewCodec),
            }),
            transformResponse: decodeResponse(LoadPreviewCodec, mapToLoadPreviewArrivals),
        }),
        postLoadPreviewUnannouncedDepartures: builder.mutation<LoadPreviewUnannouncedDepartures, LoadValidationRequest>(
            {
                query: (validationRequest) => ({
                    url: COMPOUND_MANAGEMENT_LOAD_PREVIEW_UNANNOUNCED_DEPARTURES_PATH,
                    method: 'POST',
                    body: mapToApiLoadValidationRequest(validationRequest),
                    validateStatus: validateResponse(UnannouncedLoadPreviewDeparturesCodec),
                }),
                transformResponse: decodeResponse(
                    UnannouncedLoadPreviewDeparturesCodec,
                    mapToLoadPreviewUnannouncedDepartures,
                ),
            },
        ),
        postLoadPreviewDepartures: builder.mutation<LoadPreviewDepartures, LoadValidationRequest>({
            query: (validationRequest) => ({
                url: COMPOUND_MANAGEMENT_LOAD_PREVIEW_DEPARTURES_PATH,
                method: 'POST',
                body: mapToApiLoadValidationRequest(validationRequest),
                validateStatus: validateResponse(LoadPreviewCodec),
            }),
            transformResponse: decodeResponse(LoadPreviewCodec, mapToLoadPreviewDepartures),
        }),
        putDepartureCompletion: builder.mutation<void, CompletionStatusChange>({
            query: (statusChange) => ({
                url: `${COMPOUND_MANAGEMENT_VEHICLES_PATH}/${COMPOUND_MANAGEMENT_DEPARTURE_COMPLETIONS_PATH}`,
                method: 'PUT',
                body: mapToApiStatusCompletion(statusChange),
            }),
            invalidatesTags: [DepartedVehiclesTag, OutgoingVehiclesTag, InventoryVehiclesTag],
        }),
    }),
});

export const {
    useGetIncomingVehiclesQuery,
    useGetIncomingVehicleIdsMutation,
    useGetInventoryVehiclesQuery,
    useGetInventoryVehicleIdsMutation,
    useGetOutgoingVehiclesQuery,
    useGetOutgoingVehicleIdsMutation,
    useGetDepartedVehiclesQuery,
    useGetCompoundVehiclesExportMutation,
    usePutArrivalCompletionMutation,
    usePutDepartureCompletionMutation,
    usePostLoadPreviewArrivalsMutation,
    usePostLoadPreviewUnannouncedDeparturesMutation,
    usePostLoadPreviewDeparturesMutation,
} = compoundManagementApi;

export const matchIncomingVehiclesReceived = compoundManagementApi.endpoints.getIncomingVehicles.matchFulfilled;
export const matchOutgoingVehiclesReceived = compoundManagementApi.endpoints.getOutgoingVehicles.matchFulfilled;
export const matchInventoryVehiclesReceived = compoundManagementApi.endpoints.getInventoryVehicles.matchFulfilled;
export const matchDepartedVehiclesReceived = compoundManagementApi.endpoints.getDepartedVehicles.matchFulfilled;
